"use client";
import { LoadingSpinner } from "@gettactic/components/src/loadings/LoadingSpinner/LoadingSpinner";
import { useCustomParkingVerbiageOnWeb as useCustomParkingVerbiage } from "@/lib/hooks/useCustomParkingVerbiage";
import { RESERVE_TEXTS } from "@/features/reserve/utils/utils-reserve";
import { SaveReservationData } from "@/features/reserve/utils/utils-reserve";
import { Resource } from "@gettactic/api";
import { useMemo } from "react";
import { Controller, FieldErrors, UseFormReturn } from "react-hook-form";
import { UnifiedSelect, SelectItem } from "@cmp-src/form/UnifiedSelect";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem as RadixSelectItem,
  SelectValue
} from "@cmp-src/primitives/Select";
import { useIsMobile } from "@cmp-src/hooks/use-mobile";

type ReserveFieldParkingListProps = {
  form: UseFormReturn<SaveReservationData>;
  parkingResourcesList: Resource[] | null | undefined;
  defaultValue: Resource | null;
  noParkingResource?: Resource;
  errors: FieldErrors<SaveReservationData>;
};

export function ReserveFieldParkingList({
  parkingResourcesList,
  form,
  defaultValue: def,
  noParkingResource,
  errors
}: ReserveFieldParkingListProps) {
  const isMobile = useIsMobile();

  const parkingList = useMemo(() => {
    if (!parkingResourcesList) {
      return null;
    }
    if (noParkingResource) {
      return parkingResourcesList.concat(noParkingResource);
    }
    return parkingResourcesList;
  }, [noParkingResource, parkingResourcesList]);

  type ParkingOption = Resource & { label: string; value: string };

  const options = useMemo(
    () =>
      (parkingList ?? []).map((res) => ({
        ...res,
        label: res.name,
        value: res.id
      })),
    [parkingList]
  );

  const items: SelectItem<ParkingOption>[] = useMemo(
    () =>
      options.map((res) => ({
        id: res.id,
        name: res.name,
        disabled: res.type === "area",
        data: res
      })),
    [options]
  );

  const defaultValue = useMemo(
    () => options.find((res) => res.id === def?.id),
    [def, options]
  );
  const customParkingVerbiage = useCustomParkingVerbiage();

  if (!parkingList) {
    return (
      <LoadingSpinner color="text-secondary-foreground" />
    );
  }

  const placeholder = customParkingVerbiage
    ? `Select ${customParkingVerbiage}`
    : RESERVE_TEXTS.selectParking;

  return (
    <>
      <Controller
        control={form.control}
        name={"parking_resource"}
        rules={{ required: true }}
        defaultValue={defaultValue}
        render={({ field: { onChange, value } }) => {
          const getValueContent = (data: ParkingOption) => (
            <>
              {data.name}
              {data.available_slots > 0 && (
                <span className="ml-2 rounded-full bg-green-100 px-2.5 py-0.5 font-medium text-green-800 text-xs">
                  {data.available_slots}
                </span>
              )}
            </>
          );

          const selectedItem = value
            ? (items.find((item) => item.id === value.id) ?? null)
            : null;

          return isMobile ? (
            <Select
              value={value?.id}
              onValueChange={(val) => {
                onChange({ value: options.find((res) => res.id === val) });
              }}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {options?.map((option) => (
                  <RadixSelectItem
                    key={option.id}
                    value={option.id}
                    disabled={option.type === "area"}
                  >
                    {getValueContent(option)}
                  </RadixSelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <UnifiedSelect
              items={items}
              value={selectedItem}
              onSelect={(item) => {
                if (!item || Array.isArray(item)) {
                  onChange(null);
                  return;
                }
                onChange(item.data);
              }}
              placeholder={placeholder}
              searchable
              triggerClassName="resource-select w-full bg-neutral-50 min-h-[42px] rounded-lg border-none"
              renderItem={(item) => getValueContent(item.data as ParkingOption)}
              renderValue={(selected) => {
                if (!selected || Array.isArray(selected)) return null;
                return getValueContent(selected.data as ParkingOption);
              }}
            />
          );
        }}
      />
      {errors?.parking_resource &&
        /*@ts-ignore*/
        errors.parking_resource.type === "required" && (
          <p className="text-primary">{placeholder}</p>
        )}
    </>
  );
}
